"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Container,
  Card,
  CardBody,
  Divider,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const InviteVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");

  const token = searchParams.get("token");
  const type = searchParams.get("type");

  useEffect(() => {
    const init = async () => {
      try {
        setError("");
        const { data: sessionData, error: sessionErr } =
          await supabase.auth.getSession();
        if (!sessionErr && sessionData.session?.user) {
          setUserEmail(sessionData.session.user.email || "");
          console.log(
            "[invite-verify] Session present for:",
            sessionData.session.user.email
          );
          return; // show set-password form
        }

        if (token && type === "invite") {
          console.log(
            "[invite-verify] Verifying invite token via verifyOtp..."
          );
          const { data, error: verifyErr } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: "invite",
          });

          if (verifyErr) {
            console.log(
              "[invite-verify] Token verification failed:",
              verifyErr.message
            );
            setError(
              "Invalid or expired invitation link. Please contact your administrator."
            );
            return;
          }

          if (data.user?.email) {
            setUserEmail(data.user.email);
            console.log("[invite-verify] Token verified for:", data.user.email);
            return; // show set-password form
          }
        }

        console.log(
          "[invite-verify] No session and no valid invite token; redirecting to /signin"
        );
        navigate("/signin");
      } catch (e: any) {
        console.log("[invite-verify] Unexpected error:", e?.message);
        setError("Failed to load invitation. Please try again.");
      }
    };

    init();
  }, [token, type, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      console.log(
        "[invite-verify] Setting password for:",
        userEmail || "(unknown email)"
      );

      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        console.log(
          "[invite-verify] Password update failed:",
          updateError.message
        );
        setError(updateError.message);
        return;
      }

      console.log(
        "[invite-verify] Password updated successfully, redirecting to /app"
      );
      // Keep user logged in and redirect to welcome page
      navigate("/app");
    } catch (err: any) {
      console.log("[invite-verify] Password setup error:", err?.message);
      setError(
        err?.message || "An error occurred while setting up your password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md">
        <Card boxShadow="xl" border="1px" borderColor="gray.200">
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Logo/Brand */}
              <Box textAlign="center">
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                  mx="auto"
                  mb={2}
                />
                <Text color="secondary.600" fontSize="sm">
                  Set Your Password
                </Text>
              </Box>

              {/* User Email Display */}
              {userEmail && (
                <Box w="100%" bg="gray.50" p={4} borderRadius="md">
                  <Text
                    fontSize="sm"
                    color="secondary.600"
                    fontWeight="medium"
                    mb={1}
                  >
                    Setting up account for:
                  </Text>
                  <Text
                    fontSize="md"
                    color="secondary.800"
                    fontWeight="semibold"
                  >
                    {userEmail}
                  </Text>
                </Box>
              )}

              <Divider />

              {/* Password Setup Form */}
              <Box w="100%">
                <Text mb={4} color="secondary.700" fontWeight="medium">
                  Create a secure password for your account:
                </Text>

                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel color="secondary.700">Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          bg="white"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "primary.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="secondary.700">
                        Confirm Password
                      </FormLabel>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          bg="white"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "primary.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                            icon={
                              showConfirmPassword ? (
                                <ViewOffIcon />
                              ) : (
                                <ViewIcon />
                              )
                            }
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      isLoading={isLoading}
                      loadingText="Setting up password..."
                      w="100%"
                      variant="brandGradient"
                      size="lg"
                    >
                      Set Password & Continue
                    </Button>
                  </VStack>
                </form>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default InviteVerification;
